/**
 * .tuMetrics {"tu1": 0.9, "tu2": 0.88, "tu3": 2, "notes": "TeamBots wake start; card assign is one-shot, document wakes may still poll", "tuVersion": "2.5"}
 * .help "Start currents_wake. Card assign leaves lifecycle to teambots-wake-poller; non-card wakes may poll until reply."
 */
import { getTeamBotsWakeStatus, startTeamBotsWake, type TeamBotsWakeEventType } from './api';
import useCardStore from '../../store/cardStore';
import type { CardStatus } from '../../types';
import { normalizeCardStatus } from '../../utils/normalizeCardStatus';

const POLL_MS = 5_000;
const MAX_WAIT_MS = 30 * 60_000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Patch local card status without a DB write (edge already wrote it). */
function applyLocalCardStatus(cardId: string, statusRaw: string | undefined): void {
  const status = normalizeCardStatus(statusRaw);
  if (!status || !cardId) return;

  const store = useCardStore.getState();
  const existing = store._allCards.find((c) => c.id === cardId);
  if (!existing || existing.status === status) return;

  const patch = (c: typeof existing) =>
    c.id === cardId ? { ...c, status: status as CardStatus } : c;

  useCardStore.setState((state) => {
    const _allCards = state._allCards.map(patch);
    const cards = state.cards.map(patch);
    return { _allCards, cards };
  });
}

export type WakeAndPollInput = {
  companyId: string;
  localAgentId: string;
  actorName?: string;
  senderId?: string;
  cardId?: string;
  cardUrl?: string;
  eventType?: TeamBotsWakeEventType;
  summary?: string;
  body?: string;
  entity?: { taskId: string; taskTitle: string; url?: string };
  replyTarget?: 'card' | 'dm' | 'none';
  logLabel?: string;
};

/**
 * Card assign: one-shot start. Cron `teambots-wake-poller` owns plan / Review / fail comments.
 * Never throws — assign UI must not fail because wake failed.
 */
export async function startTeamBotsWakeOnly(input: WakeAndPollInput): Promise<void> {
  const label = input.logLabel || 'wake';
  try {
    const started = await startTeamBotsWake(input);
    console.log(`[teambots ${label}] started (one-shot)`, {
      wakeId: started.wakeId,
      cardId: input.cardId,
      cardStatus: started.cardStatus,
      status: started.status,
    });
    if (input.cardId && started.cardStatus) {
      applyLocalCardStatus(input.cardId, started.cardStatus);
    }
  } catch (err) {
    console.error(`[teambots ${label}] start failed`, err);
  }
}

/**
 * Non-card wakes (document co-author DM, etc.): start + browser poll until completed/failed.
 * Prefer moving these onto the job table later; card assign must not use this path.
 */
export async function wakeAndPollTeamBots(input: WakeAndPollInput): Promise<void> {
  const label = input.logLabel || 'wake';
  try {
    const started = await startTeamBotsWake(input);
    console.log(`[teambots ${label}] started`, {
      wakeId: started.wakeId,
      cardId: input.cardId,
      cardStatus: started.cardStatus,
    });

    if (input.cardId && started.cardStatus) {
      applyLocalCardStatus(input.cardId, started.cardStatus);
    }

    const deadline = Date.now() + MAX_WAIT_MS;
    while (Date.now() < deadline) {
      await sleep(POLL_MS);
      const status = await getTeamBotsWakeStatus({
        companyId: input.companyId,
        wakeId: started.wakeId,
        cardId: input.cardId,
        localAgentId: input.localAgentId,
        senderId: input.senderId,
        replyTarget: input.replyTarget ?? (input.cardId ? 'card' : 'dm'),
      });
      if (status.status === 'completed' || status.status === 'failed') {
        console.log(`[teambots ${label}] finished`, {
          wakeId: started.wakeId,
          status: status.status,
        });
        if (input.cardId) {
          try {
            const fresh = await useCardStore.getState().fetchCardById(input.cardId);
            useCardStore.setState((state) => {
              const patch = (c: (typeof state._allCards)[number]) =>
                c.id === fresh.id ? fresh : c;
              return {
                _allCards: state._allCards.map(patch),
                cards: state.cards.map(patch),
              };
            });
          } catch (refreshErr) {
            console.warn(`[teambots ${label}] post-wake card refresh skipped`, refreshErr);
          }
        }
        return;
      }
    }
    console.warn(`[teambots ${label}] timed out still running`, { wakeId: started.wakeId });
  } catch (err) {
    console.error(`[teambots ${label}] failed`, err);
  }
}
